/**
 * Merges a published edition over productDb, producing a productDb-SHAPED price
 * book. Every existing getProductPrice call site therefore works unchanged —
 * which is what keeps the runtime switch a single injection rather than a
 * rewrite of 19 call sites.
 *
 * THIS IS AN ADAPTER, NOT A SHIM. It is deliberately the only module that knows
 * productDb's shape. The long-term direction is that productDb disappears and
 * the catalog carries SKUs, descriptions and specs; when that happens the sheet
 * grows those fields, this function's productDb argument is dropped, and nothing
 * downstream changes.
 *
 * Data gating lives in one expression: no edition -> productDb, unchanged.
 */
import { quarterToKey, quarterToDiscountKey, isGovernedSku } from './seedBuilder';
import { SHEET_FORMAT } from './publishPlan';
import type { EditionRecord } from './publishPlan';

export interface ProductDb { products: Record<string, Record<string, unknown>>; }

export function priceBookFromEdition(
  edition: EditionRecord | null, productDb: ProductDb,
): ProductDb {
  if (!edition) return productDb;
  if (edition.sheetFormat !== SHEET_FORMAT) {
    throw new Error(
      `unrecognised sheetFormat "${edition.sheetFormat}" on ${edition.id} ` +
      `(expected "${SHEET_FORMAT}") — refusing to guess money units`);
  }

  const products: Record<string, Record<string, unknown>> = {};
  for (const [sku, product] of Object.entries(productDb.products)) {
    if (!isGovernedSku(sku)) { products[sku] = product; continue; }

    // Strip every productDb pricing AND per-quarter discount key first: a
    // quarter the sheet does not price must NOT fall through to a stale number.
    const kept: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(product)) {
      if (!k.startsWith('pricing') && !k.startsWith('discountQ')) kept[k] = v;
    }

    const entry = edition.sheet[sku];
    if (entry) {
      kept.discount = entry.discount;
      for (const [quarter, dollars] of Object.entries(entry.prices)) {
        kept[quarterToKey(quarter)] = dollars;
      }
      for (const [quarter, fraction] of Object.entries(entry.discounts ?? {})) {
        kept[quarterToDiscountKey(quarter)] = fraction;
      }
    }
    products[sku] = kept;
  }
  // Spread productDb first: this is a productDb-SHAPED price book, and productDb
  // carries 27 top-level keys besides `products` — ssdPerVpod, ssdPerVelo, the
  // capacity lists, compressionRatio, vpodsPerNode and the metrics constants.
  // Returning `{ products }` alone keeps every price correct while making every
  // capacity and quantity calculation resolve `undefined` and render NaN, which
  // no price-comparing test can see.
  return { ...productDb, products };
}
